import {
  eventDefinition,
  formatParisDateTime,
  parisDayBounds,
  parisDayKey,
  statusEntries,
  UNIFIED_ORDER_STATUS,
  type EventCode,
  type UnifiedOrderStatus,
} from '@flux/shared';
import { Filter, X } from 'lucide-react';
import type { JSX } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { OriginFilter } from '@/components/filters/origin-filter.js';
import { useOperationsOptions } from './use-orders-queries.js';

export interface OrderFiltersValue {
  status?: UnifiedOrderStatus[] | undefined;
  from?: string | undefined;
  to?: string | undefined;
  search?: string | undefined;
  operationId?: string | undefined;
  withWarnings?: boolean | undefined;
  alertCode?: EventCode | undefined;
  alertFrom?: string | undefined;
  alertTo?: string | undefined;
}

export type OrderFiltersPatch = Partial<OrderFiltersValue>;

export interface OrderFiltersProps {
  value: OrderFiltersValue;
  onChange: (patch: OrderFiltersPatch) => void;
  origin: string | null;
  onOriginChange: (next: string | null) => void;
}

function dayInputValue(iso: string | undefined): string {
  return iso ? parisDayKey(new Date(iso)) : '';
}

function StatusFilter({
  value,
  onChange,
}: Pick<OrderFiltersProps, 'value' | 'onChange'>): JSX.Element {
  const selected = value.status ?? [];

  function toggle(status: UnifiedOrderStatus, checked: boolean): void {
    const next = checked ? [...selected, status] : selected.filter((code) => code !== status);
    onChange({ status: next.length > 0 ? next : undefined });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" aria-label="Filtrer par statut">
          <Filter aria-hidden="true" />
          Statut
          {selected.length > 0 ? <Badge tone="gray">{selected.length}</Badge> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <div className="flex flex-col">
          {statusEntries(UNIFIED_ORDER_STATUS).map((entry) => (
            <label
              key={entry.code}
              className="flex min-h-8 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(entry.code)}
                onCheckedChange={(checked) => {
                  toggle(entry.code, checked === true);
                }}
              />
              {entry.label}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateRangeFilter({
  value,
  onChange,
}: Pick<OrderFiltersProps, 'value' | 'onChange'>): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="orders-from" className="font-normal text-muted-foreground">
        Du
      </Label>
      <Input
        id="orders-from"
        type="date"
        className="w-36"
        value={dayInputValue(value.from)}
        onChange={(event) => {
          const day = event.target.value;
          onChange({ from: day ? parisDayBounds(day).start.toISOString() : undefined });
        }}
      />
      <Label htmlFor="orders-to" className="font-normal text-muted-foreground">
        au
      </Label>
      <Input
        id="orders-to"
        type="date"
        className="w-36"
        value={dayInputValue(value.to)}
        onChange={(event) => {
          const day = event.target.value;
          onChange({ to: day ? parisDayBounds(day).end.toISOString() : undefined });
        }}
      />
    </div>
  );
}

/** Chip retirable de l'opération commerciale, quand la liste arrive filtrée depuis une page Opération. */
function OperationChip({
  value,
  onChange,
}: Pick<OrderFiltersProps, 'value' | 'onChange'>): JSX.Element | null {
  const { data } = useOperationsOptions();
  if (value.operationId === undefined) {
    return null;
  }
  const operation = (data?.items ?? []).find((item) => item.id === value.operationId);

  return (
    <span className="inline-flex h-9 max-w-full items-center gap-1 rounded-md border border-border bg-muted pl-3 pr-1 text-sm">
      <span className="truncate">Opération : {operation?.name ?? value.operationId}</span>
      <button
        type="button"
        aria-label="Retirer le filtre d’opération"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => {
          onChange({ operationId: undefined });
        }}
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

/** Chip retirable de l'alerte, quand la liste arrive filtrée depuis « Alertes des dernières 24 h ». */
function AlertChip({
  value,
  onChange,
}: Pick<OrderFiltersProps, 'value' | 'onChange'>): JSX.Element | null {
  if (value.alertCode === undefined) {
    return null;
  }

  return (
    <span className="inline-flex h-9 max-w-full items-center gap-1 rounded-md border border-border bg-muted pl-3 pr-1 text-sm">
      <span className="truncate">
        Alerte : {eventDefinition(value.alertCode).titleFr}
        {value.alertFrom !== undefined && value.alertTo !== undefined ? (
          <span className="text-muted-foreground">
            {' '}
            (du {formatParisDateTime(new Date(value.alertFrom))} au {formatParisDateTime(new Date(value.alertTo))})
          </span>
        ) : null}
      </span>
      <button
        type="button"
        aria-label="Retirer le filtre d’alerte"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => {
          onChange({ alertCode: undefined, alertFrom: undefined, alertTo: undefined });
        }}
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

/** Filtres de l'écran Commandes (`02` §3) — rendus à droite de la recherche de `ListToolbar`, en contrôles de 36 px. */
export function OrderFilters({
  value,
  onChange,
  origin,
  onOriginChange,
}: OrderFiltersProps): JSX.Element {
  return (
    <>
      <StatusFilter value={value} onChange={onChange} />
      <DateRangeFilter value={value} onChange={onChange} />
      <OriginFilter value={origin} onChange={onOriginChange} includePennylane />
      <label className="flex h-9 cursor-pointer items-center gap-2 whitespace-nowrap px-1 text-sm">
        <Checkbox
          checked={value.withWarnings === true}
          onCheckedChange={(checked) => {
            onChange({ withWarnings: checked === true ? true : undefined });
          }}
        />
        Avec avertissements
      </label>
      <OperationChip value={value} onChange={onChange} />
      <AlertChip value={value} onChange={onChange} />
    </>
  );
}

/** `true` dès qu'un filtre (hors recherche) est posé — pilote « Réinitialiser ». */
export function hasActiveOrderFilters(value: OrderFiltersValue, origin: string | null): boolean {
  return (
    (value.status?.length ?? 0) > 0 ||
    value.from !== undefined ||
    value.to !== undefined ||
    value.operationId !== undefined ||
    value.withWarnings === true ||
    value.alertCode !== undefined ||
    origin !== null
  );
}
